import { useSelector, useDispatch } from "react-redux";
import { selectCapsules } from "../store/slices/capsules-slice";

const useHandlers = ({ selectedCapsules, setIsLoading, notifyError }) => {
  const machine = useSelector((state) => state.machine.machine);
  const selectedMachineVariantId = useSelector((state) => state.machine.selectedMachineVariantId);
  const frequency = useSelector((state) => state.frequency.frequency);

  const dispatch = useDispatch();

  const findSellingPlan = (data, targetSellingPlanGid) => {
    if (!data?.productVariant?.sellingPlanGroups) {
      console.error("Invalid data structure");
      return null;
    }

    for (const group of data.productVariant.sellingPlanGroups) {
      for (const plan of group.sellingPlans) {
        if (plan.platformId === targetSellingPlanGid) {
          return plan;
        }
      }
    }

    return null;
  };

  const getSelectedSellingPlan = async (sellingPlanGroup, frequency) => {
    const selectedPlanEntry = Object.entries(sellingPlanGroup).find(
      ([, value]) => value.title.toLowerCase().replace(/\s+/g, "-") === frequency
    );

    if (!selectedPlanEntry) {
      console.error("No selling plan matches the given frequency");
      return null;
    }

    const sellingPlan = selectedPlanEntry[1];

    const productVariantPlatformId = Object.keys(sellingPlan?.variantMapping ?? {})[0];

    if (productVariantPlatformId) {
      try {
        const response = await fetch(
          "https://api.skio.com/storefront-v2-http/get-product-variant-selling-plans",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ productVariantPlatformId }),
          }
        );
        const data = await response.json();
        const targetSellingPlanGid = selectedPlanEntry[0];

        const selectedSellingPlan = findSellingPlan(data, targetSellingPlanGid);

        if (selectedSellingPlan?.billingPolicy?.minCycles) {
          const minCycles = selectedSellingPlan.billingPolicy.minCycles;
          return { gid: selectedPlanEntry[0], plan: selectedPlanEntry[1], minCycles };
        }
        return { gid: selectedPlanEntry[0], plan: selectedPlanEntry[1] };
      } catch (error) {
        console.error("Error fetching selling plan variant details:", error);
        return null;
      }
    } else {
      console.error("No variantMapping found for this selling plan.");
      return null;
    }
  };

  const handleAddToCart = async (data1, data2) => {
    console.log("handleAddToCart data", data1, data2);
    console.log("handleAddToCart frequency", frequency);

    const selectedData = frequency.includes("deliveries-commitment") ? data2 : data1;
    const sellingPlanGroup = selectedData?.DynamicBox?.sellingPlanGroup;

    if (!sellingPlanGroup) {
      console.error("No selling plan group found");
      notifyError();
      return;
    }

    const selectedSellingPlan = await getSelectedSellingPlan(sellingPlanGroup, frequency);

    if (!selectedSellingPlan) {
      notifyError();
      return;
    }

    console.log("selectedSellingPlan", selectedSellingPlan);

    const sellingPlanId = parseInt(
      selectedSellingPlan.gid.replace("gid://shopify/SellingPlan/", ""),
      10
    );

    if (machine && frequency && selectedCapsules.length > 0) {
      const itemsToAdd = {
        items: selectedCapsules.map((capsule) => {
          const variantId = parseInt(
            capsule.platformId?.replace("gid://shopify/ProductVariant/", ""),
            10
          );
          if (!variantId) throw new Error(`Invalid variant ID for capsule: ${capsule.platformId}`);

          return {
            id: variantId,
            quantity: capsule.quantity,
            selling_plan: sellingPlanId,
            properties: {
              _dynamicBoxIndex: 0,
              _dynamicBoxId: selectedData?.DynamicBox?.boxId ?? "",
              _minCycles: selectedSellingPlan.minCycles || null,
            },
          };
        }),
      };

      if (selectedMachineVariantId) {
        itemsToAdd.items.push({
          id: selectedMachineVariantId,
          quantity: 1,
          properties: {
            _dynamicBoxMachine: true,
            _minCycles: selectedSellingPlan.minCycles || null,
          },
        });
      }

      try {
        setIsLoading(true);

        const response = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(itemsToAdd),
        });

        if (!response.ok) throw new Error(`Error adding items to cart: ${response.statusText}`);

        const cart = await response.json();
        console.log("Cart after adding items:", cart);

        setIsLoading(false);

        window.location.href = "/checkout";
      } catch (error) {
        console.error("Error adding items to cart:", error);
        setIsLoading(false);
        notifyError();
      }
    } else {
      console.error("Please select all required options");
      notifyError();
    }
  };

  const mergeCapsules = (capsules) => {
    const merged = [];

    capsules.forEach((item) => {
      const existingItem = merged.find(
        (capsule) => capsule.productPlatformId === item.productPlatformId
      );

      if (existingItem) {
        existingItem.quantity = (existingItem.quantity || 0) + (item.quantity || 0);
      } else {
        merged.push({ ...item, quantity: item.quantity || 0 });
      }
    });

    return merged;
  };

  const progressDiscount = (data) => {
    const mergedCapsules = mergeCapsules(selectedCapsules);
    // console.log("Merged Capsules:", mergedCapsules, "data", data);

    const totalItems = mergedCapsules.reduce((acc, item) => acc + item.quantity, 0);

    const percentDiscountTier = data?.DynamicBox?.percentDiscountTier || {};
    const clonedDiscountTier = { ...percentDiscountTier };

    if (!Object.isExtensible(percentDiscountTier)) {
      clonedDiscountTier[7] = "20";
      // console.log("Cloned Discount Tier:", clonedDiscountTier);
    } else {
      // console.log("Modifiable Discount Tier:", percentDiscountTier);
    }

    const discount = clonedDiscountTier[totalItems] || 0;

    return { totalItems, discount, clonedDiscountTier };
  };

  const handleQuantityChange = (product, variant, newQuantity) => {
    // console.log("handleQuantityChange", product, variant, newQuantity);
    const currentCapsule = selectedCapsules.find(
      (selected) => selected.platformId === variant.platformId
    );
    const currentQuantity = currentCapsule ? currentCapsule.quantity : 0;

    const totalItems = selectedCapsules.reduce((acc, item) => acc + item.quantity, 0);

    const updatedTotal = totalItems - currentQuantity + newQuantity;

    if (newQuantity === 0) {
      dispatch(
        selectCapsules({
          ...product,
          ...variant,
          quantity: 0,
        })
      );
    } else if (updatedTotal <= 8) {
      dispatch(
        selectCapsules({
          ...product,
          ...variant,
          quantity: newQuantity,
        })
      );
    }
  };

  return {
    handleAddToCart,
    progressDiscount,
    handleQuantityChange,
  };
};

export default useHandlers;
